import { useState, useEffect } from "react";
import { useApi } from "../context/ApiContext";
import { useAuth } from "../context/AuthContext";

interface ChatConversation {
  _id: string;
  unreadCount: number;
}

/**
 * Hook to fetch and track unread message count for admin/store_admin
 * Returns the total number of unread messages across all conversations
 */
export const useAdminUnreadMessages = (): number => {
  const { api } = useApi();
  const auth = useAuth();
  const user = auth?.user;
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Only fetch for admin, store_admin, or co_admin roles
    if (!user || (user.role !== "admin" && user.role !== "store_admin" && user.role !== "co_admin")) {
      setUnreadCount(0);
      return;
    }

    const fetchUnreadCount = async (): Promise<void> => {
      try {
        const response = await api.get("/chat/admin/conversations");
        const conversations: ChatConversation[] = response.data.conversations || [];
        const total = conversations.reduce(
          (sum, conv) => sum + (conv.unreadCount || 0),
          0
        );
        setUnreadCount(total);
      } catch (error) {
        console.error("Error fetching unread messages:", error);
        setUnreadCount(0);
      }
    };

    // Fetch immediately
    fetchUnreadCount();

    // Poll every 30 seconds to keep count updated
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [api, user]);

  return unreadCount;
};

